import React from "react";
import { Link } from "react-router-dom";
import { Ticket, CalendarDays } from "lucide-react";
import { SearchResultEvent } from "../../api/fetchEventSearchResults/types";

interface EventDirectoryCardProps {
  event: SearchResultEvent;
}

function formatNextSessionLabel(unixSeconds: number | null): string | null {
  if (unixSeconds === null) return null;
  const date = new Date(unixSeconds * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function EventDirectoryCard({ event }: EventDirectoryCardProps) {
  const DESCRIPTION_MAX_LENGTH = 120;

  const truncatedDescription = event.eventDescription
    ? event.eventDescription.length > DESCRIPTION_MAX_LENGTH
      ? event.eventDescription.substring(0, DESCRIPTION_MAX_LENGTH) + "..."
      : event.eventDescription
    : "";

  const eventLink = `/event-registration/events/${event.uuid}`;
  const nextSessionLabel = formatNextSessionLabel(event.nextSessionStartDate);

  // Cards always go to the event details page now — even for legacy-
  // redirect events. The user picks a specific session on the details
  // page and the click handler on that session row is what opens
  // Member Portal in a new tab.
  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Link className="block" to={eventLink}>
      {children}
    </Link>
  );

  return (
    <div className="block group rounded-lg transition-all bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
      <Wrapper>
        <div className="relative rounded-t-lg overflow-hidden">
          {event.thumbnailUrl ? (
            <img
              alt={event.eventName}
              className="block w-full h-auto"
              src={event.thumbnailUrl}
            />
          ) : (
            <div className="w-full aspect-video flex items-center justify-center bg-gray-100 dark:bg-gray-700">
              <span className="text-gray-500 dark:text-gray-400 text-sm">
                No Image
              </span>
            </div>
          )}

          {event.isVoucherEligible && (
            <div className="absolute bottom-2 right-2 z-10">
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-100 flex items-center gap-1">
                <Ticket className="h-3 w-3" />
                Vouchers Accepted
              </span>
            </div>
          )}
        </div>

        <div className="p-4">
          {event.eventTypeName && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary text-white">
                {event.eventTypeName}
              </span>
            </div>
          )}

          <h3 className="font-semibold text-base mb-1 dark:text-white">
            {event.eventName}
          </h3>

          {truncatedDescription && (
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
              {truncatedDescription}
            </p>
          )}

          {event.eventPrice > 0 ? (
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                ${event.eventPrice.toFixed(2)}
              </p>
              {event.isEligibleForLata && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                  Included with Total Access
                </p>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm font-medium text-green-600 dark:text-green-400">
              Free
            </p>
          )}

          <div className="mt-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
            {nextSessionLabel ? (
              <span className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
                <CalendarDays className="h-4 w-4" />
                Next: {nextSessionLabel}
              </span>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                No upcoming sessions
              </span>
            )}
          </div>
        </div>
      </Wrapper>
    </div>
  );
}
